import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Hash, Pencil, Send, X } from 'lucide-react';
import AppHeader from '../components/ui/AppHeader';
import ArchitectHeader from '../components/pm/ArchitectHeader';
import UserAvatar from '../components/ui/UserAvatar';
import { useAuth } from '../utils/AuthContext';
import { useCRM } from '../utils/CRMContext';
import { useCanUseTeamChat } from '../hooks/useCanUseTeamChat';
import { canGrantTeamChat, canUseTeamChat, isDesignPartnerOrEngineerRole, isArchitectRole } from '../utils/userRoles';
import {
  TEAM_CHAT_MEMBER_CAP,
  channelLabel,
  conversationTitle,
  eligibleChatUsers,
} from '../utils/teamChat';
import {
  createGroupChannel,
  ensureTeamRoom,
  markTeamConversationRead,
  openOrCreateDirectMessage,
  sendTeamChatMessage,
  subscribeTeamConversations,
  subscribeTeamMessages,
  subscribeTeamRead,
  type TeamChatMessage,
  type TeamConversation,
} from '../utils/teamChatService';
import './TeamChatPage.css';

const TeamChatPage: React.FC = () => {
  const { user } = useAuth();
  const { crmUsers } = useCRM();
  const allowed = useCanUseTeamChat();
  const [params, setParams] = useSearchParams();
  const selectedId = params.get('c') || '';
  const [conversations, setConversations] = useState<TeamConversation[]>([]);
  const [messages, setMessages] = useState<TeamChatMessage[]>([]);
  const [reads, setReads] = useState<Record<string, Date | null>>({});
  const [composing, setComposing] = useState(false);
  const [toIds, setToIds] = useState<string[]>([]);
  const [toQuery, setToQuery] = useState('');
  const [groupTitle, setGroupTitle] = useState('');
  const [body, setBody] = useState('');
  const [mentionQuery, setMentionQuery] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const endRef = useRef<HTMLDivElement>(null);
  const composerRef = useRef<HTMLTextAreaElement>(null);

  const people = useMemo(() => eligibleChatUsers(crmUsers), [crmUsers]);
  const nameOf = useCallback((uid: string) => (
    people.find((p) => p.uid === uid)?.displayName
    || crmUsers.find((p) => p.uid === uid)?.displayName
    || 'Someone'
  ), [people, crmUsers]);

  useEffect(() => {
    if (!allowed || !user?.id) return;
    ensureTeamRoom(user.id).catch((err) => {
      console.error('[team-chat] team room', err);
      setError('Could not open the team channel.');
    });
  }, [allowed, user?.id]);

  useEffect(() => {
    if (!allowed || !user?.id) return;
    return subscribeTeamConversations(user.id, setConversations, (err) => {
      console.error('[team-chat] conversations', err);
      setError('Could not load conversations.');
    });
  }, [allowed, user?.id]);

  const conversationIds = conversations.map((row) => row.id).join('\n');
  useEffect(() => {
    if (!user?.id || !conversationIds) return;
    const unsubs = conversationIds.split('\n').map((id) => subscribeTeamRead(id, user.id, (read) => {
      setReads((prev) => ({ ...prev, [id]: read.lastReadAt }));
    }));
    return () => unsubs.forEach((stop) => stop());
  }, [user?.id, conversationIds]);

  const selected = conversations.find((row) => row.id === selectedId) || null;

  useEffect(() => {
    if (!selectedId || composing) {
      setMessages([]);
      return;
    }
    return subscribeTeamMessages(selectedId, setMessages, (err) => {
      console.error('[team-chat] messages', err);
      setError('Could not load messages.');
    });
  }, [selectedId, composing]);

  useEffect(() => {
    endRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, selectedId, composing]);

  useEffect(() => {
    if (!user?.id || !selectedId || composing || messages.length === 0) return;
    const latest = messages[messages.length - 1]?.createdAt;
    const seen = reads[selectedId];
    if (latest && (!seen || latest > seen)) {
      markTeamConversationRead(selectedId, user.id).catch(() => undefined);
    }
  }, [user?.id, selectedId, composing, messages, reads]);

  const channels = conversations.filter((row) => row.kind !== 'dm');
  const directs = conversations.filter((row) => row.kind === 'dm');
  const orderedChannels = [...channels].sort((a, b) => {
    if (a.kind === 'team') return -1;
    if (b.kind === 'team') return 1;
    return conversationTitle(a.kind, a.title, a.memberIds, user?.id || '', nameOf)
      .localeCompare(conversationTitle(b.kind, b.title, b.memberIds, user?.id || '', nameOf));
  });

  const isUnread = (row: TeamConversation) => {
    if (!row.lastMessageAt || row.lastMessageAuthorId === user?.id) return false;
    const seen = reads[row.id];
    return !seen || row.lastMessageAt > seen;
  };

  const openConversation = (id: string) => {
    setComposing(false);
    setError('');
    setBody('');
    setParams({ c: id });
  };

  const startCompose = () => {
    setComposing(true);
    setToIds([]);
    setToQuery('');
    setGroupTitle('');
    setBody('');
    setError('');
    setParams({});
  };

  const startDirect = async (otherId: string) => {
    if (!user?.id || sending) return;
    setSending(true);
    setError('');
    try {
      const id = await openOrCreateDirectMessage(user.id, otherId);
      setComposing(false);
      setToQuery('');
      setToIds([]);
      setParams({ c: id });
    } catch (err) {
      console.error('[team-chat] direct', err);
      setError('Could not start that conversation. Team chat has to be turned on for both people.');
    } finally {
      setSending(false);
    }
  };

  const memberRefs = (ids: string[]) => ids.map((uid) => ({
    uid,
    displayName: nameOf(uid),
  }));

  const send = async () => {
    if (!user?.id || sending) return;
    const text = body.trim();
    if (!text) return;
    setSending(true);
    setError('');
    try {
      let conversationId = selectedId;
      let kind = selected?.kind || 'dm';
      let title = selected?.title || '';
      let memberIds = selected?.memberIds || [];
      if (composing) {
        if (toIds.length === 0) {
          setError('Choose who this message is for.');
          setSending(false);
          return;
        }
        if (toIds.length === 1) {
          conversationId = await openOrCreateDirectMessage(user.id, toIds[0]);
          kind = 'dm';
          title = '';
          memberIds = [user.id, toIds[0]];
        } else {
          const name = groupTitle.trim();
          if (!name) {
            setError('Name this channel.');
            setSending(false);
            return;
          }
          conversationId = await createGroupChannel({
            selfId: user.id,
            title: name,
            memberIds: toIds,
          });
          kind = 'group';
          title = name;
          memberIds = Array.from(new Set([user.id, ...toIds]));
        }
      }
      if (!conversationId) return;
      await sendTeamChatMessage({
        conversationId,
        kind,
        title,
        memberIds,
        authorId: user.id,
        authorName: user.name || 'Someone',
        body: text,
        members: memberRefs(memberIds),
      });
      setBody('');
      setMentionQuery(null);
      setComposing(false);
      setParams({ c: conversationId });
    } catch (err) {
      console.error('[team-chat] send', err);
      setError('Message was not sent.');
    } finally {
      setSending(false);
    }
  };

  const onComposerKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      void send();
    }
    if (event.key === 'Escape') setMentionQuery(null);
  };

  const onComposerChange = (value: string) => {
    setBody(value);
    const match = value.match(/(?:^|\s)@([^\n@]*)$/);
    setMentionQuery(match ? match[1] : null);
  };

  const mentionPool = (composing ? toIds : (selected?.memberIds || []))
    .filter((id) => id !== user?.id)
    .map((uid) => ({ uid, displayName: nameOf(uid) }))
    .filter((m) => !mentionQuery || m.displayName.toLowerCase().includes(mentionQuery.toLowerCase()));

  const insertMention = (displayName: string) => {
    setBody((current) => current.replace(/(?:^|\s)@([^\n@]*)$/, (full) => {
      const lead = full.startsWith(' ') || full.startsWith('\n') ? full[0] : '';
      return `${lead}@${displayName} `;
    }));
    setMentionQuery(null);
    composerRef.current?.focus();
  };

  const directory = crmUsers
    .filter((p) => (
      p.uid !== user?.id
      && canGrantTeamChat(p)
      && (!toQuery || (p.displayName || '').toLowerCase().includes(toQuery.toLowerCase()))
    ))
    .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));

  const headerLabel = composing
    ? 'New message'
    : selected
      ? channelLabel(selected.kind, conversationTitle(selected.kind, selected.title, selected.memberIds, user?.id || '', nameOf))
      : 'Chat';
  const headerMeta = selected && !composing
    ? selected.memberIds.map((id) => (id === user?.id ? 'you' : nameOf(id))).join(', ')
    : '';
  if (!user) return null;
  if (!allowed) return <Navigate to="/" replace />;

  const header = isDesignPartnerOrEngineerRole(user.role) && !isArchitectRole(user.role)
    ? (
      <ArchitectHeader
        homePath={user.role === 'engineer' ? '/designer-portal' : '/designer-portal'}
        homeLinkLabel={user.role === 'engineer' ? 'Engineer Work' : 'Design Work'}
        showStandup={user.role !== 'engineer'}
      />
    )
    : <AppHeader />;

  return (
    <>
      {header}
      <div className="tchat-shell">
        <aside className="tchat-rail">
          <div className="tchat-rail-top">
            <strong>Chat</strong>
            <button type="button" className="tchat-compose" onClick={startCompose} aria-label="New message">
              <Pencil size={16} />
            </button>
          </div>
          <div className="tchat-rail-scroll">
            <p className="tchat-section">Channels</p>
            {orderedChannels.map((row) => {
              const label = conversationTitle(row.kind, row.title, row.memberIds, user.id, nameOf);
              const unread = isUnread(row);
              return (
                <button
                  key={row.id}
                  type="button"
                  className={`tchat-row${row.id === selectedId && !composing ? ' is-active' : ''}${unread ? ' is-unread' : ''}`}
                  onClick={() => openConversation(row.id)}
                >
                  <Hash size={15} />
                  <span>{label}</span>
                  {unread && <i className="tchat-pip" aria-label="Unread" />}
                </button>
              );
            })}
            <p className="tchat-section">Direct messages</p>
            {directs.map((row) => {
              const otherId = row.memberIds.find((id) => id !== user.id) || '';
              const unread = isUnread(row);
              return (
                <button
                  key={row.id}
                  type="button"
                  className={`tchat-row${row.id === selectedId && !composing ? ' is-active' : ''}${unread ? ' is-unread' : ''}`}
                  onClick={() => openConversation(row.id)}
                >
                  <UserAvatar name={nameOf(otherId)} userId={otherId} photoURL={crmUsers.find((p) => p.uid === otherId)?.photoURL} className="tchat-avatar" />
                  <span>{nameOf(otherId)}</span>
                  {unread && <i className="tchat-pip" aria-label="Unread" />}
                </button>
              );
            })}
            {directs.length === 0 && <p className="tchat-empty">No direct messages yet.</p>}
          </div>
        </aside>
        <section className="tchat-main">
          <header className="tchat-head">
            <h1>{headerLabel}</h1>
            {headerMeta && <p>{headerMeta}</p>}
          </header>
          {composing ? (
            <div className="tchat-directory">
              <label className="tchat-search">
                To
                <input
                  value={toQuery}
                  onChange={(e) => setToQuery(e.target.value)}
                  placeholder="Search people"
                  aria-label="Search people"
                  autoFocus
                />
              </label>
              {toIds.length > 0 && (
                <div className="tchat-chips">
                  {toIds.map((id) => (
                    <button key={id} type="button" className="tchat-chip" onClick={() => setToIds((ids) => ids.filter((x) => x !== id))}>
                      {nameOf(id)}
                      <X size={12} />
                    </button>
                  ))}
                </div>
              )}
              <ul className="tchat-people">
                {directory.map((person) => {
                  const enabled = canUseTeamChat(person);
                  return (
                    <li key={person.uid}>
                      <button
                        type="button"
                        className="tchat-person"
                        onClick={() => { if (enabled) void startDirect(person.uid); }}
                        disabled={sending || !enabled}
                      >
                        <UserAvatar name={person.displayName} userId={person.uid} photoURL={person.photoURL} className="tchat-avatar tchat-avatar-lg" />
                        <span>
                          <strong>{person.displayName || 'Unnamed'}</strong>
                          <em>{enabled ? 'Direct message' : 'Team chat is off'}</em>
                        </span>
                      </button>
                      {enabled && (
                        <button
                          type="button"
                          className={`tchat-add${toIds.includes(person.uid) ? ' is-on' : ''}`}
                          onClick={() => setToIds((ids) => (
                            ids.includes(person.uid) ? ids.filter((id) => id !== person.uid) : [...ids, person.uid].slice(0, TEAM_CHAT_MEMBER_CAP - 1)
                          ))}
                        >
                          {toIds.includes(person.uid) ? 'Added' : 'Add to group'}
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
              {directory.length === 0 && (
                <p className="tchat-empty">No one matches that name.</p>
              )}
              {toIds.length > 1 && (
                <form
                  className="tchat-group-form"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void send();
                  }}
                >
                  <input
                    value={groupTitle}
                    onChange={(e) => setGroupTitle(e.target.value)}
                    placeholder="Channel name"
                    aria-label="Channel name"
                  />
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    placeholder="Write a message…"
                    rows={3}
                  />
                  <button type="submit" className="tchat-send" disabled={sending || !body.trim() || !groupTitle.trim()}>
                    <Send size={14} />
                    Start group
                  </button>
                </form>
              )}
              {error && <p className="tchat-error">{error}</p>}
            </div>
          ) : (
            <>
              <div className="tchat-feed">
                {selected && messages.length === 0 && <p className="tchat-empty">No comments yet. Start the conversation.</p>}
                {selected && messages.map((message) => (
                  <article key={message.id} className={`tchat-item${message.authorId === user.id ? ' is-own' : ''}`}>
                    <UserAvatar
                      name={message.authorName || nameOf(message.authorId)}
                      userId={message.authorId}
                      photoURL={crmUsers.find((p) => p.uid === message.authorId)?.photoURL}
                      className="tchat-avatar tchat-avatar-lg"
                    />
                    <div>
                      <p className="tchat-item-meta">
                        <strong>{message.authorName || nameOf(message.authorId)}</strong>
                        {message.createdAt && (
                          <time>{formatDistanceToNow(message.createdAt, { addSuffix: true })}</time>
                        )}
                      </p>
                      <p className="tchat-item-text">{message.body}</p>
                    </div>
                  </article>
                ))}
                {!selected && <p className="tchat-empty">Pick a channel or start a message.</p>}
                <div ref={endRef} />
              </div>
              {selected && (
                <form
                  className="tchat-composer"
                  onSubmit={(e) => {
                    e.preventDefault();
                    void send();
                  }}
                >
                  {mentionQuery !== null && mentionPool.length > 0 && (
                    <ul className="tchat-mentions">
                      {mentionPool.slice(0, 6).map((m) => (
                        <li key={m.uid}>
                          <button type="button" onClick={() => insertMention(m.displayName)}>{m.displayName}</button>
                        </li>
                      ))}
                    </ul>
                  )}
                  <textarea
                    ref={composerRef}
                    value={body}
                    rows={3}
                    placeholder="Write a message… (@ to mention, Enter to send)"
                    aria-label="Write a message"
                    onChange={(e) => onComposerChange(e.target.value)}
                    onKeyDown={onComposerKeyDown}
                  />
                  <div className="tchat-composer-row">
                    <button type="submit" className="tchat-send" disabled={sending || !body.trim()} aria-label="Send">
                      <Send size={15} />
                    </button>
                  </div>
                  {error && <p className="tchat-error">{error}</p>}
                </form>
              )}
            </>
          )}
        </section>
      </div>
    </>
  );
};

export default TeamChatPage;
